"use client";

import React, { useMemo, useState, useEffect, useRef } from "react";
import { ChevronDown, LogOut, Check, Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { fetchBridgeLanguageStrings, LogoutFromSystem, paymentInformation, userSubscriptionDetails } from "@/Services/Service";
import { decodeWithBase64 } from "@/Services/encodedecode";
import { useNavigate } from "react-router-dom";
import UpgradePage from "@/pages/Upgrade";
import { clearAllData, loadData } from "@/Services/Data";
import { LanguageStore } from "@/Services/LanguageStore";
import { BridgeLanguage } from "@/Services/Language";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

export type UserInfo = { name: string; email?: string; avatarUrl?: string };

function tryParseProfile(raw: string): any | null {
    try {
        return JSON.parse(raw);
    } catch { }
    try {
        const decoded = typeof atob === "function" ? atob(raw) : raw;
        return JSON.parse(decoded);
    } catch { }
    return null;
}

function deriveUserInfo(profile: any): UserInfo {
    const name =
        [profile?.FirstName, profile?.LastName].filter(Boolean).join(" ") ||
        profile?.UserName ||
        "User";
    const email =
        profile?.emailId || profile?.Email || profile?.email || undefined;
    const avatarUrl =
        profile?.ProfilePhoto ||
        profile?.ProfilePicture ||
        profile?.photoURL ||
        profile?.photo ||
        profile?.avatar ||
        undefined;
    return { name, email, avatarUrl };
}

function getInitials(name?: string) {
    if (!name) return "U";
    const parts = String(name).trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "U";
}

// Optional circular progress if you ever want the first style back
function CircularProgress({
    value = 52,
    size = 28,
    stroke = 3,
}: {
    value?: number;
    size?: number;
    stroke?: number;
}) {
    const clamped = Math.max(0, Math.min(100, value));
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (clamped / 100) * circumference;

    return (
        <svg width={size} height={size} className="shrink-0 text-foreground">
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke="currentColor"
                className="opacity-40"
                strokeWidth={stroke}
                fill="none"
            />
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke="currentColor"
                strokeWidth={stroke}
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
        </svg>
    );
}

function AvatarCircle({
    name,
    url,
    size = 32,
    stroke = 30,
    progress,
}: {
    name?: string;
    url?: string;
    size?: number;
    stroke?: number;
    progress?: number | undefined;
}) {
    const style: React.CSSProperties = { width: size, height: size };
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const hasProgress = typeof progress === "number" && Number.isFinite(progress);
    const clamped = hasProgress ? Math.max(0, Math.min(100, progress)) : 0;
    const offset = circumference - (clamped / 100) * circumference;

    return (
        <div className="relative shrink-0" style={style}>
            {/* Track */}
            <svg
                width={size}
                height={size}
                className="absolute inset-0 text-foreground pointer-events-none"
            >
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={stroke}
                    fill="none"
                    className="opacity-40"
                />
                {/* Progress arc */}
                {hasProgress && (
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        stroke="currentColor"
                        className="text-black"
                        strokeWidth={stroke}
                        fill="none"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    />
                )}
            </svg>

            {/* Avatar (image or initials) */}
            {url ? (
                <img
                    src={url}
                    alt={name || "User"}
                    className="rounded-full object-cover w-full h-full relative z-10"
                />
            ) : (
                <div className="rounded-full bg-muted flex items-center justify-center w-full h-full relative z-10">
                    <span className="text-[10px] font-medium">{getInitials(name)}</span>
                </div>
            )}
        </div>
    );
}
type UserMenuProps = {
    align?: "start" | "end";
    mode?: "avatar" | "progress";
    progress?: number;
    compactMax?: number;
    expandedMax?: number;
};

export const UserMenu: React.FC<UserMenuProps> = ({
    align = "end",
    mode = "avatar", // "avatar" | "progress"
    progress = 52,
    compactMax = 200, // ~ 12.5rem
    expandedMax = 320, // expands on hover/open
}) => {
    const [userInfo, setUserInfo] = React.useState<UserInfo | null>(null);
    const [totalLimits, setTotalLimits] = useState(0);
    const [usedLimits, setUsedLimits] = useState(0);
    const [showUpgrade, setShowUpgrade] = useState(false);
    const navigate = useNavigate();
    const [isUserEmployee, setUserEmployee] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [languageOpen, setLanguageOpen] = useState(false);
    const [languageList, setLanguageList] = useState([]);
    const [query, setQuery] = React.useState("");
    const [selected, setSelected] = useState(0);
    const [userTopupData, setuserTopupData] = useState<any[]>([]);

    const LanguageString = (number: any) => {
        let LangData = LanguageStore.allLanguages;
        if (Object.keys(LangData).length === 0) {
            let localLang = localStorage.getItem('BridgeLanguageStrings');
            if (localLang) {
                LangData = JSON.parse(decodeWithBase64(localLang));
            }
        }
        if (LangData && LangData[number]) {
            return LangData[number];
        }
        else {
            return BridgeLanguage(number);
        }
    }

    React.useEffect(() => {
        if (typeof window === "undefined") return;
        const raw = window.localStorage.getItem("userProfile");
        if (!raw) return;
        const profile = tryParseProfile(raw);
        if (profile) setUserInfo(deriveUserInfo(profile));
        getCreditsData()
    }, []);

    const name = userInfo?.name ?? "User";
    const email = userInfo?.email ?? "user@example.com";

    // Tailwind utility for dynamic max-width via inline style
    const compactMaxPx = `${compactMax}px`;
    const expandedMaxPx = `${expandedMax}px`;
    const percent = useMemo(() => {
        const hasTotal = Number.isFinite(totalLimits) && totalLimits > 0;
        const pct = hasTotal ? (usedLimits / totalLimits) * 100 : 0;
        return Math.max(0, Math.min(100, pct));
    }, [usedLimits, totalLimits]);

    // show progress only when we truly have both values
    const limitsReady = useMemo(
        () => Number.isFinite(totalLimits) && Number.isFinite(usedLimits) && totalLimits >= 0,
        [totalLimits, usedLimits]
    );

    const getCreditsData = async () => {
        try {
            const limitData = await paymentInformation();
            if (limitData['ErrorCode'] !== 3000) return
            const subscriptionData = await userSubscriptionDetails("Subscription");
            if (subscriptionData['ErrorCode'] === 3003) {
                const subsData = subscriptionData["ErrorMessage"];
                const userType = subsData["UserType"];
                const currencyType = subsData.Currency;
                let totalLimits = 0;
                let usedLimits = 0;
                if (userType === "Free") {
                    setUserEmployee(false);
                    totalLimits = subsData['BridgesLimit'] || 0;
                    usedLimits = subsData['BridgesUsed'] || 0;
                }
                else if ((userType === "Pro" || userType === "ProYearly") && subsData['Active']) {
                    setUserEmployee(false);
                    if (currencyType === "INR") {
                        totalLimits = subsData['BridgesLimit'] || 0;
                        usedLimits = subsData['BridgesUsed'] || 0;
                    }
                    else {
                        totalLimits = subsData['BridgesLimit'] || 0;
                        usedLimits = subsData['BridgesUsed'] || 0;
                    }
                }
                else if (userType === "Employee") {
                    setUserEmployee(true);
                    totalLimits = subsData['BridgesLimit'] || 0;
                    usedLimits = subsData['BridgesUsed'] || 0;
                }
                else {
                    setUserEmployee(false);
                    totalLimits = 0;
                    usedLimits = 0;
                }
                const BridgeAllTopupData = subsData['Topup'] || [];
                const topupData = [];
                if (Array.isArray(BridgeAllTopupData)) {
                    for (let i = 0; i < BridgeAllTopupData.length; i++) {
                        const current = BridgeAllTopupData[i];
                        const BridgesLimit = current['BridgeTopup'];
                        const BridgesUsed = current['BridgeTopupUsed'];
                        const expiresAt = current?.ExpireDate;
                        const type = current?.Type;
                        let data = {
                            BridgesLimit: typeof BridgesLimit === "string" ? Number(BridgesLimit) : BridgesLimit,
                            BridgesUsed: typeof BridgesUsed === "string" ? Number(BridgesUsed) : BridgesUsed,
                            ExpiresAt: typeof expiresAt === "string" ? Number(expiresAt) : expiresAt,
                            Type: type,
                        }
                        topupData.push(data);
                    }
                }
                setTotalLimits(totalLimits);
                setUsedLimits(usedLimits);
                setuserTopupData(topupData);
            }
        } catch (error) {

        }
    }

    // Call a method whenever the dropdown opens
    const handleMenuOpenChange = async (open: boolean) => {
        setMenuOpen(open);
        if (open) {
            // Call whatever you need on open
            await getCreditsData(); // example: refresh data when menu opens
        }
    };
    const handleLogout = async () => {
        const ok = window.confirm(LanguageString("2170"));
        if (!ok) return;
        const raw = localStorage.getItem('userProfile');
        if (!raw) return; // nothing to do
        const userData = JSON.parse(decodeWithBase64(raw));
        await LogoutFromSystem();
        try {
            if ('caches' in window) {
                const names = await caches.keys();
                await Promise.all(names.map(n => caches.delete(n)));
            }
        } catch { }
        await clearAllData()
        try { window.postMessage({ type: 'oauth_done', ok: false, base: '' }, '*'); } catch { }
        try {
            navigate('/', { replace: true });
        } catch (e) {
            window.location.assign('/');
        }
    };

    const filteredLanguages = React.useMemo(() => {
        const q = query.trim().toLowerCase()
        return (languageList || [])
            .map((label, idx) => ({ label, idx }))
            .filter(({ label }) => !q || String(label).toLowerCase().includes(q))
    }, [query, languageList])

    const applyLanguage = async () => {
        const selectedLabel = languageList?.[selected] ?? "English"

        const data = await fetchBridgeLanguageStrings(selectedLabel);
        localStorage.setItem("language", selectedLabel);
        window.location.reload();

        setLanguageOpen(false)
    }

    const handleLanguageOpen = async () => {
        try {
            let langData: any = await loadData("languageList")
            if (langData) {
                langData = JSON.parse(decodeWithBase64(langData))
            }

            const list: string[] = Array.isArray(langData) && langData.length ? langData : ["English"]
            setLanguageList(list)

            const storedLabel = localStorage.getItem("language")
            const idx = storedLabel ? list.findIndex((x) => x === storedLabel) : -1
            setSelected(idx >= 0 ? idx : 0)

            setLanguageOpen(true)
        } catch (e) {
            setLanguageList(["English"])
            setSelected(0)
            setLanguageOpen(true)
        }
    }

    return (
        <>
            <DropdownMenu open={menuOpen} onOpenChange={handleMenuOpenChange}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        aria-label="User menu"
                        className="group border h-10 px-2 pr-2 gap-2 bg-muted/40 hover:bg-muted/70 transition-all data-[state=open]:shadow-sm"
                        style={{
                            maxWidth: compactMaxPx,
                        }}
                    >
                        {mode === "avatar" ? (
                            <AvatarCircle
                                name={name}
                                url={userInfo?.avatarUrl}
                                size={32}
                                progress={limitsReady ? percent : undefined}
                            />
                        ) : limitsReady ? (
                            <CircularProgress value={percent} size={28} />
                        ) : (
                            // fallback (no progress yet): show plain avatar
                            <AvatarCircle name={name} url={userInfo?.avatarUrl} size={32} />
                        )}

                        <div
                            className="min-w-0 text-left leading-tight"
                            style={{
                                maxWidth: compactMaxPx,
                            }}
                        >
                            <div className="text-sm font-medium truncate">{name}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{email}</div>
                        </div>

                        <ChevronDown className="h-4 w-4 opacity-70 shrink-0" />

                        <style>{`
            button.group:hover,
            button.group[data-state='open'] {
              max-width: ${expandedMaxPx};
            }
          `}</style>
                    </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align={align} className="w-72 p-3">
                    <div className="flex items-center gap-3">
                        <AvatarCircle
                            name={name}
                            url={userInfo?.avatarUrl}
                            size={40}
                            progress={limitsReady ? percent : undefined}
                        />
                        <div className="grid">
                            <span className="text-sm font-semibold">{name}</span>
                            {email ? (
                                <span className="text-xs text-muted-foreground">{email}</span>
                            ) : null}
                        </div>
                    </div>

                    <DropdownMenuItem
                        onSelect={(e) => {
                            e.preventDefault();
                            setMenuOpen(false);
                            handleLanguageOpen();
                        }}
                        className="cursor-pointer p-2"
                    >
                        <div className="flex items-center gap-3">
                            <Languages className="h-10 w-4 mr-2 ml-3" />
                            <div className="grid">
                                <span className="text-sm font-semibold">{LanguageString("Language")}</span>
                            </div>
                        </div>
                    </DropdownMenuItem>

                    <DropdownMenuItem
                        onSelect={(e) => {
                            e.preventDefault();
                            setMenuOpen(false);
                            handleLogout();
                        }}
                        className="cursor-pointer p-2"
                    >
                        <div className="flex items-center gap-3">
                            <LogOut className="h-10 w-4 mr-2 ml-3" />
                            <div className="grid">
                                <span className="text-sm font-semibold">{LanguageString("LogOut")}</span>
                            </div>
                        </div>
                    </DropdownMenuItem>


                    {/* Credits used */}
                    <div className="mt-3 pt-3 border-t">
                        {(usedLimits > 0 || totalLimits > 0) ? (
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">{LanguageString("2171")}</span>
                                <span className="text-sm font-medium">
                                    {usedLimits} of {totalLimits}
                                </span>
                            </div>
                        ) : (
                            !isUserEmployee ? (
                                <>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground">{LanguageString("2077")}</span>
                                    </div>
                                    <Button
                                        size="sm"
                                        className="mt-3 w-full"
                                        onClick={() => { setMenuOpen(false); setShowUpgrade(true); }}
                                    >
                                        {LanguageString("1673")}
                                    </Button>
                                </>
                            ) : (
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-muted-foreground">{LanguageString("2172")}</span>
                                </div>
                            )
                        )}
                        {limitsReady && (
                            <div className="mt-2 h-1.5 w-full bg-muted rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-black rounded-full"
                                    style={{ width: `${percent}%` }}
                                />
                            </div>
                        )}

                        {userTopupData && userTopupData.length > 0 && (
                            <div className="mt-3 max-h-40 overflow-y-auto pr-1 space-y-3">
                                {userTopupData.map((topup, index) => {
                                    const topupLimit = topup.BridgesLimit || 0;
                                    const topupUsed = topup.BridgesUsed || 0;
                                    const topupPercent = topupLimit > 0 ? (topupUsed / topupLimit) * 100 : 0;
                                    const cappedPercent = Math.max(0, Math.min(100, topupPercent));

                                    let expiryStr = "";
                                    if (topup.ExpiresAt) {
                                        const isMilliseconds = topup.ExpiresAt > 9999999999;
                                        const date = new Date(isMilliseconds ? topup.ExpiresAt : topup.ExpiresAt * 1000);
                                        if (!isNaN(date.getTime())) {
                                            expiryStr = ` - Expires: ${date.toLocaleDateString()}`;
                                        }
                                    }

                                    return (
                                        <div key={index} className="pt-2 border-t border-dashed">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-muted-foreground">
                                                    {topup.Type || "Topup"}{expiryStr}
                                                </span>
                                                <span className="text-sm font-medium">
                                                    {topupUsed} of {topupLimit}
                                                </span>
                                            </div>
                                            <div className="mt-2 h-1.5 w-full bg-muted rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-black rounded-full"
                                                    style={{ width: `${cappedPercent}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {limitsReady && percent >= 85 && !isUserEmployee && (
                            <Button
                                size="sm"
                                className="mt-3 w-full"
                                onClick={() => { setMenuOpen(false); setShowUpgrade(true); }}
                            >
                                {LanguageString("2173")}
                            </Button>
                        )}
                        {isUserEmployee && limitsReady && percent >= 100 && (
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">{LanguageString("2172")}</span>
                            </div>
                        )}
                    </div>

                </DropdownMenuContent>
            </DropdownMenu>
            <Dialog open={languageOpen} onOpenChange={setLanguageOpen}>
                <DialogContent className="sm:max-w-[420px]">
                    <DialogHeader>
                        <DialogTitle>{LanguageString("Language")}</DialogTitle>
                        <DialogDescription>{LanguageString("588")}</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3">
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={LanguageString("1976")}
                        />

                        <div className="max-h-[280px] overflow-auto rounded-md border border-border bg-muted/20 p-2">
                            <div className="space-y-1">
                                {filteredLanguages.map(({ label, idx }) => {
                                    const active = selected === idx
                                    return (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => setSelected(idx)}
                                            className={[
                                                "w-full rounded-md px-3 py-2 text-left text-sm",
                                                "hover:bg-muted/60 flex items-center justify-between",
                                                active ? "bg-muted" : "bg-transparent",
                                            ].join(" ")}
                                        >
                                            <span className="truncate">{label}</span>
                                            {active ? <Check className="h-4 w-4 shrink-0" /> : null}
                                        </button>
                                    )
                                })}

                                {filteredLanguages.length === 0 ? (
                                    <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                                        {LanguageString("1977")}
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setLanguageOpen(false)}>
                                {LanguageString("1463")}
                            </Button>
                            <Button onClick={applyLanguage}>{LanguageString("Apply")}</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {showUpgrade && <UpgradePage showUpgrade={showUpgrade} setShowUpgrade={setShowUpgrade} />}
        </>
    );
};

export default UserMenu;


